import React, { useState, useMemo, useEffect } from 'react';
import { 
  Drawer, Box, Typography, IconButton, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import { getNumberComma } from '../../utils/numberUtils';

const SalesDetailDrawer = ({ open, onClose, selectedDetail, data }) => {
  console.log(selectedDetail);
  const [tabValue, setTabValue] = useState('ALL');

  useEffect(() => {
    if (open) setTabValue('ALL');
  }, [open]);

  const uniqueTypes = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...new Set(data.map(item => item.prjTyp))].filter(Boolean);
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (tabValue === 'ALL') return data;
    return data.filter(item => item.prjTyp === tabValue);
  }, [data, tabValue]);

  const totalAmount = useMemo(() => {
    return filteredData.reduce((acc, cur) => acc + (Number(cur.invAmt) || 0), 0);
  }, [filteredData]);

  const getProjectLink = (row) => {
    console.log(row);
    const { prjUuid, prjTyp } = row;
    if (!prjUuid) return null;

    switch (prjTyp) {
      case 'N':   return `/projectmng/${prjUuid}/2/9`;
      case 'M':   return `/maintancetmng/${prjUuid}/2/9`;
      case 'ETC': return `/etcmng/${prjUuid}/2/9`;
      default:    return null;
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 850 }, padding: '24px' }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {selectedDetail?.field?.id === 'sales' ? '매출 상세 내역' : '매입 상세 내역'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {selectedDetail && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 1, mb: 2, border: '1px solid #eee' }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>담당자:</strong> {selectedDetail.cellData.name}
            </Typography>
            <Typography variant="body2">
              <strong>항목:</strong> {selectedDetail.type.name}
            </Typography>
          </Box>
          {uniqueTypes.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              {uniqueTypes.length > 1 ? (
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  variant="fullWidth"
                  sx={{ minHeight: '40px', '& .MuiTab-root': { fontSize: '0.85rem', fontWeight: 700, py: 1 } }}
                >
                  <Tab label="전체" value="ALL" />
                  {uniqueTypes.includes('N') && <Tab label="프로젝트" value="N" />}
                  {uniqueTypes.includes('M') && <Tab label="유지보수" value="M" />}
                  {uniqueTypes.includes('ETC') && <Tab label="기타" value="ETC" />}
                </Tabs>
              ) : (
                <Tabs 
                  value={0}
                  variant="fullWidth"
                  sx={{ minHeight: '40px', '& .MuiTab-root': { fontSize: '0.85rem', fontWeight: 700, py: 1 } }}
                >
                  <Tab 
                    label={
                      uniqueTypes[0] === 'N' ? "프로젝트" : 
                      uniqueTypes[0] === 'M' ? "유지보수" : "기타"
                    } 
                    sx={{ color: '#1967d2 !important', opacity: 1 }}
                  />
                </Tabs>
              )}
            </Box>
          )}
          <TableContainer component={Paper} sx={{ 
            boxShadow: 'none', 
            border: '1px solid #eee', 
            flexGrow: 1, 
            maxHeight: uniqueTypes.length > 1 ? 'calc(100vh - 420px)' : 'calc(100vh - 360px)', 
            overflowY: 'auto' 
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '110px' }}>
                    날짜
                  </TableCell>
                  
                  <TableCell align="center" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    {['collaborationBenefits', 'collaborationCost'].includes(selectedDetail?.type?.id) 
                      ? '프로젝트명' 
                      : '계산서 발행 내용'}
                  </TableCell>
                  
                  <TableCell align="center" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '130px' }}>
                    금액
                  </TableCell>
                  {['collaborationBenefits', 'collaborationCost'].includes(selectedDetail?.type?.id) && (
                    <TableCell 
                      align="center" 
                      sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '130px' }}
                    >
                      {selectedDetail?.type?.id === 'collaborationBenefits' ? '영업담당' : '협업담당'}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData && filteredData.length > 0 ? (
                  filteredData.map((row, index) => {
                    const projectLink = getProjectLink(row);
                    return (
                      <TableRow key={index} hover>
                        <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                          {row.reqCompDt || row.req_comp_dt || '-'}
                        </TableCell>
                        
                        <TableCell align="left" sx={{ fontSize: '0.8rem' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {projectLink ? (
                              <Link
                                href={
                                  ['collaborationBenefits', 'collaborationCost'].includes(selectedDetail?.type?.id)
                                    ? `${projectLink.slice(0, -1)}4`
                                    : projectLink
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: '#1967d2', 
                                  textDecoration: 'underline', 
                                  display: 'flex',
                                  alignItems: 'center', 
                                  gap: 0.5, 
                                  cursor: 'pointer'
                                }}
                              >
                                {['collaborationBenefits', 'collaborationCost'].includes(selectedDetail?.type?.id)
                                  ? (row.cstNm || row.cst_nm || '상세보기') 
                                  : (row.invTxt || row.inv_txt || '상세보기')
                                }
                                <LaunchIcon sx={{ fontSize: '0.85rem' }} />
                              </Link>
                            ) : (
                              <span style={{ whiteSpace: 'pre-wrap' }}>
                                {['collaborationBenefits', 'collaborationCost'].includes(selectedDetail?.type?.id)
                                  ? (row.cstNm || row.cst_nm || '-')
                                  : (row.invTxt || row.inv_txt || '-')
                                }
                              </span>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          {getNumberComma(row.invAmt || row.inv_amt)}
                        </TableCell>
                        {['collaborationBenefits', 'collaborationCost'].includes(selectedDetail?.type?.id) && (
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                            {row.crtUsr || '-'} 
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow><TableCell colSpan={3} align="center">데이터가 없습니다.</TableCell></TableRow>
                )}

              </TableBody>
            </Table>
          </TableContainer>

          {filteredData.length > 0 && (
            <Box sx={{ 
              mt: 1, p: 2, bgcolor: '#fffde7', border: '1px solid #ffe57f', borderRadius: 1,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
            }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>선택 항목 합계 ({filteredData.length}건)</Typography>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#d32f2f' }}>
                {getNumberComma(totalAmount)} 원
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={onClose} sx={{ color: '#455a64', borderColor: '#cfd8dc', fontWeight: 700 }}>
              닫기
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default SalesDetailDrawer;
